import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CtaButtonComponent } from '../../cta-button/cta-button.component';
import {
  disableWheelReminder,
  disableWheelReminderFailure,
  disableWheelReminderSuccess,
  upsertWheelReminder,
  upsertWheelReminderFailure,
  upsertWheelReminderSuccess,
} from '../../../state';

@Component({
  selector: 'wil-set-reminder-dialog',
  standalone: true,
  imports: [MatDialogModule, MatFormFieldModule, MatSelectModule, ReactiveFormsModule, TranslateModule, CtaButtonComponent],
  templateUrl: './set-reminder-dialog.component.html',
  styleUrl: './set-reminder-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetReminderDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<SetReminderDialogComponent>);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly translate = inject(TranslateService);

  public readonly data: SetReminderDialogData = inject(MAT_DIALOG_DATA);
  public readonly isSubmitting = signal(false);
  public readonly interval = new FormControl<ReminderInterval | null>(this.data.reminder?.interval ?? null, Validators.required);
  public readonly intervals: readonly { value: ReminderInterval; label: string }[] = [
    { value: 'ONE_DAY', label: 'reminderIntervalDay' },
    { value: 'ONE_WEEK', label: 'reminderIntervalWeek' },
    { value: 'TWO_WEEKS', label: 'reminderIntervalTwoWeeks' },
    { value: 'ONE_MONTH', label: 'reminderIntervalMonth' },
    { value: 'THREE_MONTHS', label: 'reminderIntervalThreeMonths' },
    { value: 'SIX_MONTHS', label: 'reminderIntervalSixMonths' },
    { value: 'ONE_YEAR', label: 'reminderIntervalYear' },
  ];
  public readonly formattedNextReminderAt = this.formatNextReminderAt();

  public ngOnInit(): void {
    this.actions$
      .pipe(ofType(upsertWheelReminderSuccess, disableWheelReminderSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.close(true));

    this.actions$
      .pipe(ofType(upsertWheelReminderFailure, disableWheelReminderFailure), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.isSubmitting.set(false));
  }

  public cancel(): void {
    if (!this.isSubmitting()) this.dialogRef.close();
  }

  public submit(): void {
    const interval = this.interval.value;
    if (!interval || this.isSubmitting()) return;

    this.isSubmitting.set(true);
    this.store.dispatch(
      upsertWheelReminder({
        wheelId: this.data.wheelId,
        reminder: { interval, timezone: this.getBrowserTimezone() },
        isUpdate: Boolean(this.data.reminder),
      })
    );
  }

  public disable(): void {
    if (!this.data.reminder || this.isSubmitting()) return;

    this.isSubmitting.set(true);
    this.store.dispatch(disableWheelReminder({ wheelId: this.data.wheelId }));
  }

  private getBrowserTimezone(): string {
    return isPlatformBrowser(this.platformId) ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC';
  }

  private formatNextReminderAt(): string | null {
    if (!this.data.reminder?.nextReminderAt) return null;

    return new Intl.DateTimeFormat(this.translate.currentLang || undefined, { dateStyle: 'medium' }).format(
      new Date(this.data.reminder.nextReminderAt)
    );
  }
}
